import type { CompanyExportField, ExportDataType, ProfileExportField } from '@shared/schemas';

import type { FilterGroup } from '@/types/filters';
import type { InfraFailure } from '@/utils/infra-error';

import type { DeactivatedExportScope } from '../domain/build-export-request';
import {
  DEFAULT_DATA_TYPES,
  defaultCompanyColumns,
  defaultProfileColumns,
  EMPTY_FILTER_GROUP,
  type ExportFormat,
} from '../domain/export-plan.model';

/**
 * Redux state for the export wizard (Choose Format → Choose Data → Choose
 * Columns → done). One slice serves FOUR routes: client/admin × list-scoped
 * (`listId` set) / global profiles export (`listId` null). The route context
 * (filters, count, base name) arrives via `location.state` and is pinned by
 * `formInitialized` on mount; a direct visit legitimately yields the empty
 * filter and a zero count.
 */
export interface ProfileExportState {
  // ── Route context (pinned by formInitialized) ──
  /** List export: the list to scope on. Global export: null. */
  listId: string | null;
  filters: FilterGroup;
  /** Number of profiles the export covers (display; capped at 5000). */
  profileCount: number;
  /** Raw file base name (list name or page name); slugged by the domain. */
  baseName: string;
  /**
   * Set only when the wizard was opened from Deactivated Profiles (#536): it
   * exports that list under those filters, and `filters` / `listId` are then
   * both empty. Null on every other route.
   */
  deactivated: DeactivatedExportScope | null;
  /** Set only by the HNWIs page (PI-117): the licence the export is held to. */
  licenceId: string | null;

  // ── Wizard ──
  /** 0 = Choose Format, 1 = Choose Data, 2 = Choose Columns. */
  step: number;
  done: boolean;
  format: ExportFormat;
  /** At least one always selected. */
  dataTypes: ExportDataType[];
  profileColumns: ProfileExportField[];
  companyColumns: CompanyExportField[];

  exporting: boolean;
  error: InfraFailure | null;
}

export const initialState: ProfileExportState = {
  listId: null,
  filters: EMPTY_FILTER_GROUP,
  profileCount: 0,
  baseName: 'export',
  deactivated: null,
  licenceId: null,
  step: 0,
  done: false,
  format: 'csv',
  dataTypes: DEFAULT_DATA_TYPES,
  profileColumns: defaultProfileColumns(),
  companyColumns: defaultCompanyColumns(),
  exporting: false,
  error: null,
};
